import React from 'react';
import styled from 'styled-components';
import { X } from 'lucide-react';
import { axiomTheme, toneAlpha, toneColor, type Tone } from '../../styles/designTokens';

export type AxiomButtonVariant = 'primary' | 'secondary' | 'danger' | 'quiet' | 'ghost';
export type AxiomButtonSize = 'sm' | 'md' | 'lg';

const buttonBackground = (variant: AxiomButtonVariant) => {
  if (variant === 'primary') return 'linear-gradient(135deg, rgba(14, 116, 144, 0.92), rgba(37, 99, 235, 0.72))';
  if (variant === 'danger') return 'rgba(127, 29, 29, 0.28)';
  if (variant === 'secondary') return 'rgba(15, 23, 42, 0.76)';
  if (variant === 'ghost') return 'transparent';
  return 'rgba(2, 6, 23, 0.34)';
};

const buttonColor = (variant: AxiomButtonVariant) => {
  if (variant === 'danger') return '#fecaca';
  if (variant === 'primary') return '#e0f2fe';
  if (variant === 'ghost') return axiomTheme.color.text.muted;
  return axiomTheme.color.text.secondary;
};

const buttonBorder = (variant: AxiomButtonVariant) => {
  if (variant === 'primary') return axiomTheme.color.border.focus;
  if (variant === 'danger') return 'rgba(248, 113, 113, 0.45)';
  if (variant === 'ghost') return 'transparent';
  return axiomTheme.color.border.default;
};

const buttonPadding = (size: AxiomButtonSize) => {
  if (size === 'sm') return '0.42rem 0.62rem';
  if (size === 'lg') return '0.72rem 1rem';
  return '0.54rem 0.78rem';
};

export const AxiomButton = styled.button<{ $variant?: AxiomButtonVariant; $size?: AxiomButtonSize }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.42rem;
  border: 1px solid ${p => buttonBorder(p.$variant || 'secondary')};
  border-radius: ${axiomTheme.radius.md};
  background: ${p => buttonBackground(p.$variant || 'secondary')};
  color: ${p => buttonColor(p.$variant || 'secondary')};
  padding: ${p => buttonPadding(p.$size || 'md')};
  font-size: ${p => p.$size === 'lg' ? '0.82rem' : p.$size === 'sm' ? '0.66rem' : '0.72rem'};
  font-weight: 850;
  letter-spacing: 0.03em;
  line-height: 1;
  text-transform: uppercase;
  cursor: pointer;
  transition: border-color 0.16s ease, background 0.16s ease, color 0.16s ease, transform 0.16s ease;

  svg {
    width: ${p => p.$size === 'lg' ? '18px' : '15px'};
    height: ${p => p.$size === 'lg' ? '18px' : '15px'};
  }

  &:hover {
    border-color: ${axiomTheme.color.border.strong};
    color: ${axiomTheme.color.text.primary};
    transform: translateY(-1px);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.48;
    transform: none;
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${axiomTheme.shadow.focus};
  }
`;

export const AxiomIconButton = styled(AxiomButton).attrs({ $size: 'sm' as AxiomButtonSize })`
  width: 32px;
  height: 32px;
  padding: 0;
`;

export const AxiomFieldLabel = styled.label`
  display: block;
  color: ${axiomTheme.color.text.muted};
  font-size: ${axiomTheme.typography.label.size};
  font-weight: ${axiomTheme.typography.label.weight};
  letter-spacing: ${axiomTheme.typography.label.tracking};
  text-transform: uppercase;
  margin-bottom: 0.38rem;
`;

const fieldStyles = `
  width: 100%;
  box-sizing: border-box;
  border: 1px solid ${axiomTheme.color.border.default};
  border-radius: ${axiomTheme.radius.lg};
  background: ${axiomTheme.surface.input};
  color: ${axiomTheme.color.text.primary};
  min-height: 38px;
  padding: 0.58rem 0.72rem;
  font-size: 0.78rem;
  outline: none;
  transition: border-color 0.16s ease, box-shadow 0.16s ease, background 0.16s ease;

  &::placeholder {
    color: ${axiomTheme.color.text.faint};
  }

  &:focus {
    border-color: ${axiomTheme.color.border.focus};
    box-shadow: ${axiomTheme.shadow.focus};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

export const AxiomInput = styled.input`
  ${fieldStyles}
`;

export const AxiomSelect = styled.select`
  ${fieldStyles}
  appearance: none;
  cursor: pointer;
`;

export const AxiomDateField = styled.input.attrs({ type: 'date' })`
  ${fieldStyles}
`;

export const AxiomTextArea = styled.textarea`
  ${fieldStyles}
  min-height: 96px;
  resize: vertical;
`;

export const AxiomActionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

export const AxiomStatusBadge = styled.span<{ $tone?: Tone }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  border: 1px solid ${p => toneAlpha(p.$tone || 'slate', '50')};
  border-radius: ${axiomTheme.radius.pill};
  background: ${p => toneAlpha(p.$tone || 'slate', '18')};
  color: ${p => toneColor(p.$tone || 'slate')};
  padding: 0.28rem 0.56rem;
  font-size: 0.64rem;
  font-weight: 850;
  letter-spacing: 0.06em;
  line-height: 1;
  text-transform: uppercase;
`;

export const AxiomMetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 0.75rem;
`;

const MetricShell = styled.div<{ $tone?: Tone; $interactive?: boolean }>`
  border: 1px solid ${axiomTheme.color.border.default};
  border-left: 3px solid ${p => toneColor(p.$tone || 'cyan')};
  border-radius: ${axiomTheme.radius.xl};
  background: ${axiomTheme.surface.card};
  padding: 0.86rem;
  min-width: 0;
  cursor: ${p => p.$interactive ? 'pointer' : 'default'};
  transition: border-color 0.16s ease, background 0.16s ease, transform 0.16s ease;

  &:hover {
    border-color: ${p => p.$interactive ? toneAlpha(p.$tone || 'cyan', '66') : axiomTheme.color.border.default};
    transform: ${p => p.$interactive ? 'translateY(-1px)' : 'none'};
  }
`;

const MetricTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
`;

const MetricIcon = styled.div<{ $tone?: Tone }>`
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border: 1px solid ${p => toneAlpha(p.$tone || 'cyan', '32')};
  border-radius: ${axiomTheme.radius.lg};
  background: ${p => toneAlpha(p.$tone || 'cyan', '12')};
  color: ${p => toneColor(p.$tone || 'cyan')};

  svg {
    width: 17px;
    height: 17px;
  }
`;

const MetricLabelText = styled.div`
  color: ${axiomTheme.color.text.muted};
  font-size: ${axiomTheme.typography.label.size};
  font-weight: ${axiomTheme.typography.label.weight};
  letter-spacing: ${axiomTheme.typography.label.tracking};
  text-transform: uppercase;
`;

const MetricValueText = styled.div`
  color: ${axiomTheme.color.text.primary};
  margin-top: 0.28rem;
  font-size: 1.22rem;
  font-weight: 850;
  letter-spacing: -0.02em;
  overflow-wrap: anywhere;
`;

const MetricSubtextText = styled.div<{ $tone?: Tone }>`
  color: ${p => p.$tone ? toneColor(p.$tone) : axiomTheme.color.text.faint};
  margin-top: 0.3rem;
  font-size: 0.69rem;
  font-weight: 650;
  line-height: 1.35;
`;

export interface AxiomMetricCardProps {
  label: string;
  value: React.ReactNode;
  subtext?: React.ReactNode;
  icon?: React.ReactNode;
  tone?: Tone;
  onClick?: () => void;
}

export const AxiomMetricCard: React.FC<AxiomMetricCardProps> = ({
  label,
  value,
  subtext,
  icon,
  tone = 'cyan',
  onClick,
}) => (
  <MetricShell $tone={tone} $interactive={!!onClick} onClick={onClick} role={onClick ? 'button' : undefined} tabIndex={onClick ? 0 : undefined}>
    <MetricTop>
      <div>
        <MetricLabelText>{label}</MetricLabelText>
        <MetricValueText>{value}</MetricValueText>
      </div>
      {icon && <MetricIcon $tone={tone}>{icon}</MetricIcon>}
    </MetricTop>
    {subtext && <MetricSubtextText>{subtext}</MetricSubtextText>}
  </MetricShell>
);

export const AxiomEmptyState = styled.div`
  border: 1px dashed ${axiomTheme.color.border.strong};
  border-radius: ${axiomTheme.radius.xl};
  background: rgba(15, 23, 42, 0.34);
  color: ${axiomTheme.color.text.muted};
  padding: 1.4rem;
  text-align: center;
  font-size: 0.82rem;
`;

export const AxiomTimeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`;

export const AxiomTimelineItem = styled.div<{ $tone?: Tone }>`
  border-left: 3px solid ${p => toneColor(p.$tone || 'cyan')};
  border-radius: 0 ${axiomTheme.radius.lg} ${axiomTheme.radius.lg} 0;
  background: rgba(15, 23, 42, 0.58);
  padding: 0.78rem 0.9rem;
`;

export const AxiomModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1300;
  display: grid;
  place-items: center;
  background: rgba(2, 6, 23, 0.72);
  backdrop-filter: blur(4px);
`;

export const AxiomModalShell = styled.div`
  width: min(680px, 94vw);
  border: 1px solid ${axiomTheme.color.border.strong};
  border-radius: ${axiomTheme.radius.xxl};
  background: ${axiomTheme.color.canvas};
  box-shadow: ${axiomTheme.shadow.drawer};
  color: ${axiomTheme.color.text.secondary};
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem 1.1rem;
  border-bottom: 1px solid ${axiomTheme.color.border.subtle};
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: ${axiomTheme.color.text.primary};
  font-size: 1rem;
  font-weight: 850;
`;

const ModalBody = styled.div`
  padding: 1rem 1.1rem;
`;

export const AxiomModal: React.FC<{
  open: boolean;
  title: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
  onClose: () => void;
}> = ({ open, title, children, actions, onClose }) => {
  if (!open) return null;
  return (
    <AxiomModalBackdrop>
      <AxiomModalShell>
        <ModalHeader>
          <ModalTitle>{title}</ModalTitle>
          <AxiomIconButton type="button" $variant="quiet" aria-label="Close modal" onClick={onClose}>
            <X />
          </AxiomIconButton>
        </ModalHeader>
        <ModalBody>
          {children}
          {actions && <AxiomActionBar style={{ marginTop: '1rem' }}>{actions}</AxiomActionBar>}
        </ModalBody>
      </AxiomModalShell>
    </AxiomModalBackdrop>
  );
};

